import { useParams, Link } from 'react-router-dom';
import SEOHelmet from '@/components/feature/SEOHelmet';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { articles, getArticleBySlug, getRelatedArticles } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? getArticleBySlug(slug) : undefined;

  if (!article) {
    const allSlugs = articles.map((a) => a.slug);
    const randomSlug = allSlugs[Math.floor(Math.random() * allSlugs.length)];

    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
              <i className="ri-article-line text-3xl text-foreground-400"></i>
            </div>
            <h2 className="text-xl font-heading font-bold text-foreground-900 mb-2">文章不存在</h2>
            <p className="text-sm text-foreground-500 mb-6">这篇爆料可能已被删除或链接有误</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link to="/" className="px-6 py-2.5 bg-primary-500 text-white rounded-lg font-medium hover:bg-primary-600 transition-colors whitespace-nowrap">
                返回首页
              </Link>
              <Link to={`/article/${randomSlug}`} className="px-6 py-2.5 border border-background-300 text-foreground-700 rounded-lg font-medium hover:bg-background-100 transition-colors whitespace-nowrap">
                随机吃瓜
              </Link>
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const relatedArticles = getRelatedArticles(article, 4);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.coverImage,
    datePublished: article.publishDate,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    publisher: {
      '@type': 'Organization',
      name: '黑料不打烊',
      url: 'https://www.heiliaobudayang.com',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://www.heiliaobudayang.com/article/${article.slug}`,
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: 'https://www.heiliaobudayang.com',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: article.categoryName,
        item: `https://www.heiliaobudayang.com/category/${article.categoryId === 'cat-1' ? 'star-scandal' : article.categoryId === 'cat-2' ? 'romance-exposure' : article.categoryId === 'cat-3' ? 'variety-insider' : article.categoryId === 'cat-4' ? 'film-behind' : article.categoryId === 'cat-5' ? 'fashion-flop' : 'legal-trouble'}`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: article.title,
        item: `https://www.heiliaobudayang.com/article/${article.slug}`,
      },
    ],
  };

  return (
    <>
      <SEOHelmet
        title={`${article.title} - 黑料不打烊`}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`https://www.heiliaobudayang.com/article/${article.slug}`}
        ogType="article"
        ogImage={article.coverImage}
        schema={{ ...articleSchema, ...breadcrumbSchema }}
      />
      <ScrollToTop />
      <Navbar />
      <main className="pt-20 pb-16">
        <article className="max-w-4xl mx-auto px-4 md:px-6">
          <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-8 flex-wrap" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-500 transition-colors whitespace-nowrap">首页</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <Link
              to={`/category/${
                article.categoryId === 'cat-1' ? 'star-scandal' :
                article.categoryId === 'cat-2' ? 'romance-exposure' :
                article.categoryId === 'cat-3' ? 'variety-insider' :
                article.categoryId === 'cat-4' ? 'film-behind' :
                article.categoryId === 'cat-5' ? 'fashion-flop' : 'legal-trouble'
              }`}
              className="hover:text-primary-500 transition-colors whitespace-nowrap"
            >
              {article.categoryName}
            </Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-800 font-medium line-clamp-1">{article.title}</span>
          </nav>

          <header className="mb-8">
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              <Link
                to={`/category/${
                  article.categoryId === 'cat-1' ? 'star-scandal' :
                  article.categoryId === 'cat-2' ? 'romance-exposure' :
                  article.categoryId === 'cat-3' ? 'variety-insider' :
                  article.categoryId === 'cat-4' ? 'film-behind' :
                  article.categoryId === 'cat-5' ? 'fashion-flop' : 'legal-trouble'
                }`}
                className="px-3 py-1 bg-primary-100 text-primary-700 text-xs font-bold rounded-full hover:bg-primary-200 transition-colors whitespace-nowrap"
              >
                {article.categoryName}
              </Link>
              {article.hot && (
                <span className="px-3 py-1 bg-accent-100 text-accent-700 text-xs font-bold rounded-full whitespace-nowrap">
                  <i className="ri-fire-line mr-1"></i>HOT
                </span>
              )}
            </div>

            <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 leading-tight mb-4">
              {article.title}
            </h1>

            <div className="flex items-center gap-4 text-sm text-foreground-500 flex-wrap">
              <span className="flex items-center gap-1 whitespace-nowrap">
                <i className="ri-user-line"></i>{article.author}
              </span>
              <span className="flex items-center gap-1 whitespace-nowrap">
                <i className="ri-calendar-line"></i>{article.publishDate}
              </span>
              <span className="flex items-center gap-1 whitespace-nowrap">
                <i className="ri-time-line"></i>阅读约{article.readTime}分钟
              </span>
              <span className="flex items-center gap-1 whitespace-nowrap">
                <i className="ri-eye-line"></i>{(article.viewCount / 10000).toFixed(0)}万阅读
              </span>
            </div>
          </header>

          <div className="mb-8 rounded-lg overflow-hidden">
            <img
              src={article.coverImage}
              alt={article.title}
              className="w-full h-auto max-h-[500px] object-cover object-top"
            />
          </div>

          <div
            className="prose-custom text-foreground-800 leading-relaxed"
            dangerouslySetInnerHTML={{
              __html: article.content
                .replace(/<p>/g, '<p class="mb-4 text-base md:text-lg leading-loose">')
                .replace(/<strong>/g, '<strong class="font-bold text-foreground-950">'),
            }}
          />

          <div className="mt-10 pt-6 border-t border-background-200/70">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-foreground-500 mr-2">标签：</span>
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 bg-background-100 text-foreground-600 text-xs rounded-full whitespace-nowrap"
                >
                  #{tag}
                </span>
              ))}
            </div>
          </div>

          <div className="mt-10 pt-8 border-t border-background-200/70 text-center">
            <p className="text-sm text-foreground-500 mb-3">
              声明：本文内容来源于多方信源整理，仅供娱乐参考，不代表本平台立场。
            </p>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="max-w-4xl mx-auto px-4 md:px-6 mt-16">
            <div className="mb-6">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-1 h-5 bg-accent-500 rounded-full"></div>
                <h2 className="font-heading text-xl font-bold text-foreground-950">相关爆料</h2>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {relatedArticles.map((rel) => (
                <Link
                  key={rel.id}
                  to={`/article/${rel.slug}`}
                  className="group bg-background-50 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all duration-300"
                >
                  <div className="h-36 overflow-hidden">
                    <img
                      src={rel.coverImage}
                      alt={rel.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-3">
                    <span className="text-xs text-primary-600 font-medium">{rel.categoryName}</span>
                    <h3 className="text-sm font-bold text-foreground-900 mt-1 line-clamp-2 group-hover:text-primary-600 transition-colors">
                      {rel.title}
                    </h3>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}